import * as React from "react";

const MEWS_SRC = "https://app.mews.com/distributor/distributor.min.js";
const MEWS_HOTEL_ID = "f5b41a45-66a7-4731-b32b-b2650000dfb8";

type MewsDistributorApi = {
  open: () => void;
  close?: () => void;
};

declare global {
  interface Window {
    Mews?: {
      Distributor: (
        config: unknown,
        onReady?: ((api: MewsDistributorApi) => void) | null,
      ) => void;
    };
    __mewsLoading?: Promise<void>;
    __mewsInitialized?: boolean;
    __mewsDistributorReady?: Promise<MewsDistributorApi>;
    __mewsDistributorApi?: MewsDistributorApi;
  }
}

/**
 * Lazily inject the Mews distributor script. The iframe is initialized once,
 * and buttons open it through the provider API instead of document-level
 * click bindings so close handlers do not stack across route changes.
 */
function loadMews(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.__mewsLoading) return window.__mewsLoading;

  window.__mewsLoading = new Promise<void>((resolve, reject) => {
    if (window.Mews) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = MEWS_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Mews"));
    document.head.appendChild(script);
  });
  return window.__mewsLoading;
}

function removeDuplicateMewsIframes() {
  if (typeof document === "undefined") return;
  const frames = Array.from(document.querySelectorAll<HTMLIFrameElement>("iframe.mews-distributor"));
  frames.slice(0, -1).forEach((frame) => frame.remove());
}

function initMews(): Promise<MewsDistributorApi> {
  if (window.__mewsDistributorApi) return Promise.resolve(window.__mewsDistributorApi);
  if (window.__mewsDistributorReady) return window.__mewsDistributorReady;

  window.__mewsDistributorReady = new Promise<MewsDistributorApi>((resolve, reject) => {
    try {
      if (!window.Mews?.Distributor) {
        reject(new Error("Mews distributor is not available"));
        return;
      }
      window.Mews.Distributor(
        { configurationIds: [MEWS_HOTEL_ID] },
        (api) => {
          window.__mewsInitialized = true;
          window.__mewsDistributorApi = api;
          removeDuplicateMewsIframes();
          resolve(api);
        },
      );
    } catch (error) {
      window.__mewsDistributorReady = undefined;
      reject(error);
    }
  });

  return window.__mewsDistributorReady;
}

export interface BookingButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  beforeOpen?: () => void | Promise<void>;
}

export async function openBookingWidget(beforeOpen?: () => void | Promise<void>) {
  await beforeOpen?.();
  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
  const api = await loadMews().then(initMews);
  api.open();
}

function ensureMewsPreconnect() {
  if (typeof document === "undefined") return;
  if (document.getElementById("mews-preconnect")) return;
  const link = document.createElement("link");
  link.id = "mews-preconnect";
  link.rel = "preconnect";
  link.href = "https://app.mews.com";
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}

function warmMews() {
  if (typeof window === "undefined") return;
  ensureMewsPreconnect();
  loadMews().then(initMews).catch(() => {});
}

function scheduleIntentWarm() {
  if (typeof window === "undefined") return () => {};
  let done = false;
  const fire = () => {
    if (done) return;
    done = true;
    cleanup();
    warmMews();
  };
  const onScroll = () => fire();
  window.addEventListener("scroll", onScroll, { passive: true, once: true });
  const timer = window.setTimeout(fire, 3000);
  function cleanup() {
    window.removeEventListener("scroll", onScroll);
    window.clearTimeout(timer);
  }
  return cleanup;
}


export const BookingButton = React.forwardRef<HTMLButtonElement, BookingButtonProps>(
  ({ className, type = "button", children, onClick, beforeOpen, onMouseEnter, onFocus, onTouchStart, ...props }, ref) => {
    React.useEffect(() => {
      ensureMewsPreconnect();
      // Preconnect only on mount. Warm the full script on real user intent
      // (hover/focus/touch) or as a low-priority fallback after scroll / 3s idle,
      // so Mews doesn't compete with LCP on first paint.
      const cleanup = scheduleIntentWarm();
      return cleanup;
    }, []);

    return (
      <button
        ref={ref}
        type={type}
        className={className}
        onMouseEnter={(e) => {
          onMouseEnter?.(e);
          warmMews();
        }}
        onFocus={(e) => {
          onFocus?.(e);
          warmMews();
        }}
        onTouchStart={(e) => {
          onTouchStart?.(e);
          warmMews();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
          openBookingWidget(beforeOpen).catch(() => {});
        }}
        {...props}
      >
        {children}
      </button>
    );
  },
);
BookingButton.displayName = "BookingButton";

export default BookingButton;
